import React from 'react';
import { FormikProvider, Form, FormikProps } from 'formik';
import {  RadioGroup, FormControlLabel, Radio, FormLabel, Grid, Button, FormControl } from '@mui/material';
import { VoucherSettingsAuto } from './VoucherSettingsAuto';
import { TDSAutocomplete } from '../../CompanycodeTranslation/components';
import { AccountsAutocomplete } from '../../AccountsAndGroups';
import { MemoTextField, MoneyTextField } from '../../../components';
import { PaymentFormState } from '../types';
import dayjs from 'dayjs';

interface ReceiptFormProps {
  type: 'Reciept' | 'Payment';
  formik: FormikProps<PaymentFormState>;
}

const ReceiptForm: React.FC<ReceiptFormProps> = ({ type, formik }) => {
  const { getFieldProps, touched, errors, values, setFieldValue } = formik;

  // Helper function to calculate TDS amount
  const calculateTdsAmount = (payableAmount: number, tdsPercentage: number) => {
    if (payableAmount > 0 && tdsPercentage > 0) {
      return ((payableAmount * tdsPercentage) / 100).toFixed(2);
    }
    return '';
  };
  console.log({values});

  return (
    <>
      <FormikProvider value={formik}>
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth component="fieldset" sx={{ border: '1px solid #c4c4c4', borderRadius: 1, height: '48px' }}>
                <FormLabel
                  component="legend"
                  sx={{
                    position: 'absolute',
                    top: -10,
                    left: 10,
                    px: 0.5,
                    fontSize: 12,
                  }}
                >
                  Payment From
                </FormLabel>
                <RadioGroup row {...getFieldProps('method')} className="p-3">
                  <FormControlLabel key={'CashPayment'} value={'CashPayment'} control={<Radio />} label={'Cash'} />
                  <FormControlLabel key={'BankPayment'} value={'BankPayment'} control={<Radio />} label={'Bank'} />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <MemoTextField
                fullWidth
                type="date"
                label="Payment Date"
                InputLabelProps={{ shrink: true }}
                value={values.date ? dayjs.unix(values.date).format('YYYY-MM-DD') : ''}
                onChange={(e) => {
                  const timestamp = e.target.value ? dayjs(e.target.value).unix() : null;
                  setFieldValue('date', timestamp);
                }}
                error={touched.date && Boolean(errors.date)}
                helperText={touched.date && errors.date ? String(errors.date) : ''}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <VoucherSettingsAuto
                name="vouchernumber"
                label="Payment Number Series"
                type={type}
                onChange={({ mannual, value }) => {
                  setFieldValue('manualvoucher', mannual);
                  setFieldValue('vouchernumber', value);
                }}
                value={values.vouchernumber}
                helperText={touched.vouchernumber && errors.vouchernumber ? String(errors.vouchernumber) : ''}
              />
            </Grid>{' '}
            <Grid item xs={12} md={3}>
              <MemoTextField
                fullWidth
                label="Bank Reference Number"
                disabled={values.method === 'CashPayment'}
                {...getFieldProps('bankRefNo')}
              />
            </Grid>
            <Grid item container xs={12} spacing={2}>
              <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                <Grid item xs={12}>
                  JOURNAL ENTRY
                </Grid>

                <Grid item xs={12}>
                  <MemoTextField fullWidth label="Remarks" {...getFieldProps('remarks')} />
                </Grid>
              </Grid>
              <Grid item container xs={12} md={6} spacing={2} order={{ xs: 2, md: 1 }}>
                <Grid item container xs={12}>
                  <Grid item xs={8}>
                    <AccountsAutocomplete
                      value={values.paidto ? Number(values.paidto) : null}
                      onChange={(account) => {
                        setFieldValue('paidto', account?.accountsId?.toString() || '');
                        
                        if (account) {
                          // Set isParty based on accountTypeCd
                          const accountTypeCd = account?.accountTypeCd;
                          const isParty = accountTypeCd === '1' || accountTypeCd === '2';
                          setFieldValue('isParty', isParty);
                          
                          // Clear TDS values when account type is not 1 or 2
                          if (!isParty) {
                            setFieldValue('tdssection', '');
                            setFieldValue('tdspercentage', '');
                            setFieldValue('tdsamount', '');
                          }
                        } else {
                          // When account is cleared, reset isParty and clear TDS values
                          setFieldValue('isParty', false);
                          setFieldValue('tdssection', '');
                          setFieldValue('tdspercentage', '');
                          setFieldValue('tdsamount', '');
                        }
                      }}
                      priorityGroupIds={[7, 15]}
                      label="Paid To"
                      error={touched.paidto && Boolean(errors.paidto)}
                      helperText={touched.paidto && errors.paidto ? String(errors.paidto) : ''}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <MoneyTextField
                      fullWidth
                      name="payableamount"
                      label="Payable Amount"
                      value={values.payableamount ? Number(values.payableamount) : null}
                      onChange={(val) => {
                        setFieldValue('payableamount', val?.toString() || '');
                        
                        // Auto-calculate TDS amount if conditions are met
                        const payableAmount = parseFloat(val?.toString() || '0');
                        const tdsPercentage = parseFloat(values.tdspercentage) || 0;
                        const tdsManual = values.tdsmanual || 0;
                        
                        if (values.tdssection && tdsPercentage > 0 && tdsManual === 0 && payableAmount > 0) {
                          const calculatedTdsAmount = calculateTdsAmount(payableAmount, tdsPercentage);
                          setFieldValue('tdsamount', calculatedTdsAmount);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      error={touched.payableamount && Boolean(errors.payableamount)}
                      helperText={touched.payableamount && errors.payableamount ? String(errors.payableamount) : ''}
                    />
                  </Grid>
                </Grid>

                <Grid item container xs={12}>
                  <Grid item xs={6}>
                    <TDSAutocomplete
                      fullWidth
                      label="TDS Section"
                      value={values.tdssection}
                      disabled={!values.isParty}
                      onChange={(option) => {
                        // Save section and percentage
                        setFieldValue('tdssection', option?.value || '');
                        setFieldValue('tdspercentage', option?.percentage || '');
                        // Reset manual flag when TDS section is selected (automatic selection)
                        setFieldValue('tdsmanual', 0);
                        
                        // Auto-calculate TDS amount if payable amount exists
                        const payableAmount = parseFloat(values.payableamount) || 0;
                        const tdsPercentage = parseFloat(String(option?.percentage || '0'));
                        
                        if (payableAmount > 0 && tdsPercentage > 0) {
                          const calculatedTdsAmount = calculateTdsAmount(payableAmount, tdsPercentage);
                          setFieldValue('tdsamount', calculatedTdsAmount);
                        }
                      }}
                      error={touched.tdssection && Boolean(errors.tdssection)}
                      helperText={touched.tdssection && errors.tdssection ? String(errors.tdssection) : ''}
                    />
                  </Grid>
               
                  <Grid item xs={2}>
                    <MemoTextField 
                      fullWidth 
                      label="TDS %" 
                      disabled={!values.isParty}
                      {...getFieldProps('tdspercentage')}
                      onChange={(e) => {
                        formik.handleChange(e);
                        // Set manual flag when user manually edits TDS percentage
                        setFieldValue('tdsmanual', 1);
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <MoneyTextField
                      fullWidth
                      name="tdsamount"
                      label="TDS Amount"
                      disabled={!values.isParty}
                      value={values.tdsamount ? Number(values.tdsamount) : null}
                      onChange={(val) => {
                        const payableAmount = parseFloat(values.payableamount) || 0;
                        const tdsAmount = parseFloat(val?.toString() || '0');
                        
                        // Real-time validation: prevent typing TDS amount >= payable amount
                        if (payableAmount > 0 && tdsAmount >= payableAmount) {
                          // Don't update the value if it's >= payable amount
                          return;
                        }
                        
                        setFieldValue('tdsamount', val?.toString() || '');
                        // Set manual flag when user manually edits TDS amount
                        setFieldValue('tdsmanual', 1);
                      }}
                      onBlur={formik.handleBlur}
                      error={touched.tdsamount && Boolean(errors.tdsamount)}
                      helperText={touched.tdsamount && errors.tdsamount ? String(errors.tdsamount) : ''}
                    />
                  </Grid>
                </Grid>
                <Grid item container xs={12}>
                  <Grid item xs={8}>
                    <AccountsAutocomplete
                      value={values.paidfrom ? Number(values.paidfrom) : null}
                      onChange={(account) => {
                        setFieldValue('paidfrom', account?.accountsId?.toString() || '');
                      }}
                      label="Paid From"
                      includeOnlyGroupIds={values.method === 'CashPayment' ? [16] : [29, 17]}
                      error={touched.paidfrom && Boolean(errors.paidfrom)}
                      helperText={touched.paidfrom && errors.paidfrom ? String(errors.paidfrom) : ''}
                    />
                  </Grid>
                  <Grid item xs={4}></Grid>
                </Grid>
                <Grid item xs={12}>
                  <MemoTextField
                    fullWidth
                    label="Towards"
                    {...getFieldProps('towards')}
                    onChange={(e) => {
                      console.log('Towards field changed:', e.target.value);
                      formik.handleChange(e);
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        </Form>
      </FormikProvider>
    </>
  );
};

export { ReceiptForm };
